'use es6';

import { createElement as _createElement } from "react";
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { Component } from 'react';
import PropTypes from 'prop-types';
import CustomWidgetPropTypes from 'DesignData/propTypes/customWidgets';
import classNames from 'classnames';
import { connect } from 'react-redux';
import text from 'I18n/utils/unescapedText';
import FormattedMessage from 'I18n/components/FormattedMessage';
import UIFlex from 'UIComponents/layout/UIFlex';
import UIDragHandle from 'UIComponents/draggable/UIDragHandle';
import UIDropdown from 'UIComponents/dropdown/UIDropdown';
import UIList from 'UIComponents/list/UIList';
import UIButton from 'UIComponents/button/UIButton';
import UITruncateString from 'UIComponents/text/UITruncateString';
import CreateFieldList from 'DesignEditorsUI/components/customWidgetEditor/sidebar/CreateFieldList';
import Small from 'UIComponents/elements/Small';
import UITooltip from 'UIComponents/tooltip/UITooltip';
import CustomWidgetField from 'DesignData/models/CustomWidgetField';
import { GROUP } from 'ContentUtils/constants/CustomWidgetFieldTypes';
import * as customWidgetBuffersSelectors from 'DesignData/selectors/customWidgetBuffers';
import { getFieldCreateOptions } from 'DesignData/selectors/customWidgetEditorOptions';
import { MAX_NESTING_DEPTH } from 'DesignData/constants/CustomWidgets';
import * as CustomWidgetBufferActions from 'DesignData/actions/CustomWidgetBuffers';
import * as CustomWidgetEditorOptionsActions from 'DesignData/actions/CustomWidgetEditorOptions';
import { fieldsTableRowDragSource, fieldsTableRowDropTarget } from 'DesignEditorsUI/components/customWidgetEditor/sidebar/main/fieldsTable/FieldsTableRowDragDrop';
import FieldGroupDropZone from 'DesignEditorsUI/components/customWidgetEditor/sidebar/main/fieldsTable/FieldGroupDropZone';
import ErrorIcon from 'DesignEditorsUI/components/customWidgetEditor/sidebar/ErrorIcon';
import WarningIcon from 'DesignEditorsUI/components/customWidgetEditor/sidebar/WarningIcon';
import CopySnippetButton from 'DesignEditorsUI/components/customWidgetEditor/sidebar/CopySnippetButton';
import { compose } from 'redux';

const i18nKey = key => `DesignEditorsUI.customWidgetEditor.sidebar.main.fields.fieldRow.${key}`;

const makeMapStateToProps = () => {
  const getCustomWidgetBuffer = customWidgetBuffersSelectors.makeGetCustomWidgetBuffer();
  return (state, props) => {
    const customWidget = getCustomWidgetBuffer(state, props);
    return {
      contentTypes: customWidget.contentTypes || [],
      hostTemplateTypes: customWidget.hostTemplateTypes || [],
      fieldCreateOptions: getFieldCreateOptions(state, props),
      fieldErrors: customWidgetBuffersSelectors.getFieldErrors(state, props),
      fieldWarnings: customWidgetBuffersSelectors.getFieldWarnings(state, props)
    };
  };
};

const mapDispatchToProps = {
  createField: CustomWidgetBufferActions.createField,
  cloneField: CustomWidgetBufferActions.cloneField,
  deleteField: CustomWidgetBufferActions.deleteField,
  moveField: CustomWidgetBufferActions.moveCustomWidgetField,
  setSelectedField: CustomWidgetEditorOptionsActions.setSelectedField,
  toggleFieldGroupingSelection: CustomWidgetEditorOptionsActions.toggleFieldGroupingSelection
};

class FieldsTableRow extends Component {
  constructor(props) {
    super(props);

    this.onAddFieldClick = () => {
      this.setState({
        openFieldsList: true
      });
    };

    this.onFieldCreate = fieldType => {
      const {
        createField,
        customWidgetId,
        field,
        fieldCreateOptions
      } = this.props;
      const newField = new CustomWidgetField({
        type: fieldType,
        isNew: true,
        fieldCreateOptions
      });
      createField(customWidgetId, newField, field.id);
    };

    this.handleClose = () => {
      this.setState({
        openActionsDropDown: false,
        openFieldsList: false
      });
    };

    this.handleOpenChange = evt => {
      const {
        value: open
      } = evt.target;

      if (open !== this.state.openActionsDropDown) {
        this.setState({
          openActionsDropDown: open,
          openFieldsList: open ? this.state.openFieldsList : false
        });
      }
    };

    this.onPopoverOpen = () => {
      this.setState({
        showActionButtons: true
      });
    };

    this.onPopoverClose = () => {
      this.setState({
        showActionButtons: false
      });
    };

    this.onCloneClick = this.onCloneClick.bind(this);
    this.onDeleteClick = this.onDeleteClick.bind(this);
    this.onEditClick = this.onEditClick.bind(this);
    this.onRowClick = this.onRowClick.bind(this);
    this.state = {
      isOverBottom: false,
      isOverTop: false,
      openActionsDropDown: false,
      openFieldsList: false,
      showActionButtons: false
    };
  }

  onCloneClick() {
    this.props.cloneField(this.props.customWidgetId, this.props.field.id);
    this.handleClose();
  }

  onDeleteClick() {
    const {
      customWidgetId,
      deleteField,
      field
    } = this.props;
    deleteField(customWidgetId, field.id);
    this.handleClose();
  }

  onEditClick() {
    const {
      customWidgetId,
      setSelectedField,
      field
    } = this.props;
    setSelectedField(customWidgetId, {
      id: field.id
    });
    this.handleClose();
  }

  onRowClick() {
    const {
      toggleFieldGroupingSelection,
      customWidgetId,
      fieldGrouping,
      field
    } = this.props;

    if (!fieldGrouping.isActive) {
      return;
    }

    toggleFieldGroupingSelection(customWidgetId, field);
  }

  renderDragHandle() {
    const {
      connectDragSource,
      depth,
      fieldErrors,
      fieldWarnings,
      field,
      fieldGrouping,
      isDraggable,
      isReadOnly
    } = this.props;
    const iconClassname = 'm-right-2' + (depth === MAX_NESTING_DEPTH - 1 ? " max-depth-error-icon" : "");

    const labelAndName = /*#__PURE__*/_jsxs(UIFlex, {
      align: "center",
      className: "p-left-7",
      children: [/*#__PURE__*/_jsx(ErrorIcon, {
        className: iconClassname,
        fieldErrors: fieldErrors
      }), /*#__PURE__*/_jsx(WarningIcon, {
        className: iconClassname,
        fieldWarnings: fieldWarnings
      }), /*#__PURE__*/_jsxs(UIFlex, {
        direction: "column",
        children: [/*#__PURE__*/_jsx(Small, {
          className: "field-label",
          use: field.label ? null : 'help',
          children: /*#__PURE__*/_jsxs(UITruncateString, {
            children: [field.label || text(i18nKey('missingLabel')), /*#__PURE__*/_jsxs("span", {
              className: "m-left-1",
              children: ["(", field.getFieldTypeLabel(), ")"]
            })]
          })
        }), /*#__PURE__*/_jsx(Small, {
          className: "m-top-1 field-name is--code is--text--help",
          use: "help",
          children: /*#__PURE__*/_jsx(UITruncateString, {
            children: field.name
          })
        })]
      })]
    });

    if (isReadOnly || !isDraggable || fieldGrouping.isActive) {
      return /*#__PURE__*/_jsx("div", {
        className: "p-left-2",
        children: labelAndName
      });
    }

    return connectDragSource( /*#__PURE__*/_jsx("div", {
      className: "p-left-2",
      children: /*#__PURE__*/_jsx(UIDragHandle, {
        children: labelAndName
      })
    }));
  }

  renderActionContents() {
    const {
      field,
      customWidgetId,
      canCreateFieldType,
      canEditFieldType,
      isStyleTab
    } = this.props;

    if (this.state.openFieldsList) {
      return /*#__PURE__*/_jsx(CreateFieldList, {
        customWidgetId: customWidgetId,
        onFieldCreate: this.onFieldCreate,
        isStyleTab: isStyleTab
      });
    }

    const cloneI18nKey = field.type === GROUP ? 'actions.cloneGroup' : 'actions.cloneField';
    const deleteI18nKey = field.type === GROUP ? 'actions.deleteGroup' : 'actions.deleteField';

    let cloneButton = /*#__PURE__*/_jsx(UIButton, {
      disabled: !canCreateFieldType,
      onClick: this.onCloneClick,
      use: "link",
      children: /*#__PURE__*/_jsx(FormattedMessage, {
        message: i18nKey(cloneI18nKey)
      })
    });

    if (!canCreateFieldType) {
      const cloneButtonI18nKey = canEditFieldType ? 'missingCreatePermission' : 'missingEditAndCreatePermission';
      cloneButton = /*#__PURE__*/_jsx(UITooltip, {
        title: text(i18nKey(cloneButtonI18nKey)),
        children: cloneButton
      });
    }

    const fieldName = this.props.fieldName || field.name;
    return /*#__PURE__*/_jsxs(UIList, {
      children: [/*#__PURE__*/_jsx(CopySnippetButton, {
        customWidgetId: this.props.customWidgetId,
        hostTemplateTypes: this.props.hostTemplateTypes,
        contentTypes: this.props.contentTypes,
        field: field,
        fieldName: fieldName
      }), /*#__PURE__*/_jsx(CopySnippetButton, {
        customWidgetId: this.props.customWidgetId,
        hostTemplateTypes: this.props.hostTemplateTypes,
        contentTypes: this.props.contentTypes,
        field: field,
        fieldName: fieldName,
        useFullSnippet: false
      }), cloneButton, /*#__PURE__*/_jsx(UIButton, {
        onClick: this.onDeleteClick,
        use: "link",
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: i18nKey(deleteI18nKey)
        })
      }), field.type === GROUP && /*#__PURE__*/_jsx(UIButton, {
        onClick: this.onAddFieldClick,
        use: "link",
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: i18nKey('actions.addField')
        })
      })]
    });
  }

  renderActionsList() {
    const {
      isReadOnly
    } = this.props;
    const {
      openActionsDropDown,
      openFieldsList
    } = this.state;

    const actionsButton = /*#__PURE__*/_jsx(Small, {
      children: /*#__PURE__*/_jsx(FormattedMessage, {
        message: i18nKey('actions.label')
      })
    });

    return /*#__PURE__*/_jsx(UIDropdown, {
      buttonSize: "extra-small",
      buttonText: actionsButton,
      buttonUse: "link",
      closeOnMenuClick: false,
      disabled: isReadOnly,
      onOpenChange: this.handleOpenChange,
      open: openActionsDropDown,
      placement: "bottom left",
      popoverProps: {
        onOpenComplete: this.onPopoverOpen,
        onCloseComplete: this.onPopoverClose
      },
      use: openFieldsList ? 'unstyled' : 'list',
      children: this.renderActionContents()
    });
  }

  renderActions() {
    const {
      canEditFieldType,
      canCreateFieldType,
      isReadOnly,
      fieldGrouping
    } = this.props;
    if (fieldGrouping.isActive) return null;
    const editButtonText = isReadOnly ? /*#__PURE__*/_jsx(FormattedMessage, {
      message: i18nKey('actions.viewField')
    }) : /*#__PURE__*/_jsx(FormattedMessage, {
      message: i18nKey('actions.editField')
    });

    let editButton = /*#__PURE__*/_jsx(UIButton, {
      disabled: !canEditFieldType,
      onClick: this.onEditClick,
      use: "link",
      children: /*#__PURE__*/_jsx(Small, {
        children: editButtonText
      })
    });

    if (!canEditFieldType) {
      const editButtonI18nKey = canCreateFieldType ? 'missingEditPermission' : 'missingEditAndCreatePermission';
      editButton = /*#__PURE__*/_jsx(UITooltip, {
        title: text(i18nKey(editButtonI18nKey)),
        children: editButton
      });
    }

    const actionsClassName = 'field-row-actions-dropdown' + (this.state.showActionButtons ? " actions-dropdown-selected" : "");
    return /*#__PURE__*/_jsxs("span", {
      className: actionsClassName,
      children: [editButton, this.renderActionsList()]
    });
  }

  renderChildren() {
    const {
      customWidgetId,
      field,
      fieldName,
      moveField,
      depth,
      isStyleTab
    } = this.props;

    if (field.type !== GROUP) {
      return null;
    }

    if (!field.children || !field.children.length) {
      return depth < MAX_NESTING_DEPTH - 1 ? /*#__PURE__*/_jsx("div", {
        className: "field-children p-top-0 p-x-4 p-bottom-4",
        children: /*#__PURE__*/_jsx(FieldGroupDropZone, {
          customWidgetId: customWidgetId,
          moveField: moveField,
          parentField: field,
          isStyleTab: isStyleTab
        })
      }) : null;
    }

    return /*#__PURE__*/_jsx("div", {
      className: "field-children p-top-0 p-x-4 p-bottom-4",
      children: field.children.map(_child => {
        const _fieldName = `${fieldName || field.name}.${_child.name}`;
        return /*#__PURE__*/_createElement(DraggableFieldsTableRow, Object.assign({}, this.props, {
          depth: depth + 1,
          field: _child,
          fieldName: _fieldName,
          isChild: true,
          key: `fields-table-row-${_child.id}`
        }));
      })
    });
  }

  renderRow() {
    return /*#__PURE__*/_jsx("div", {
      className: "field-row",
      onClick: this.onRowClick,
      children: /*#__PURE__*/_jsxs(UIFlex, {
        align: "center",
        className: "p-y-3 p-right-4",
        justify: "between",
        children: [this.renderDragHandle(), this.renderActions()]
      })
    });
  }

  render() {
    const {
      connectDragPreview,
      connectDropTarget,
      isDragging,
      isChild,
      isOver,
      fieldGrouping,
      field,
      canDrop
    } = this.props;
    const {
      selectedFields = {},
      isActive,
      fieldsAreNotSiblings,
      isTooDeep
    } = fieldGrouping;
    const isInvalidGrouping = fieldsAreNotSiblings || isTooDeep;
    const isSelected = selectedFields[field.id];
    const className = classNames('field-row-and-children-container', isOver && [this.state.isOverBottom && 'is--over-bottom', this.state.isOverTop && 'is--over-top', !canDrop && 'is--cannot-drop'], isSelected && (isInvalidGrouping ? 'is--invalid-selected' : 'is--selected'), isChild && 'is--child-field', isDragging && 'is--dragging', isActive && 'is--in-group-mode');
    return connectDropTarget(connectDragPreview( /*#__PURE__*/_jsxs("div", {
      className: className,
      children: [this.renderRow(), this.renderChildren()]
    })));
  }

}

FieldsTableRow.propTypes = {
  canEditFieldType: PropTypes.bool,
  canDrop: PropTypes.bool,
  canCreateFieldType: PropTypes.bool,
  connectDragPreview: PropTypes.func.isRequired,
  connectDragSource: PropTypes.func.isRequired,
  connectDropTarget: PropTypes.func.isRequired,
  contentTypes: PropTypes.array,
  customWidgetId: CustomWidgetPropTypes.customWidgetId.isRequired,
  hostTemplateTypes: PropTypes.array,
  cloneField: PropTypes.func.isRequired,
  deleteField: PropTypes.func.isRequired,
  depth: PropTypes.number.isRequired,
  field: PropTypes.instanceOf(CustomWidgetField).isRequired,
  fieldCreateOptions: PropTypes.object.isRequired,
  fieldGrouping: PropTypes.object.isRequired,
  fieldName: PropTypes.string,
  fieldErrors: PropTypes.object,
  fieldWarnings: PropTypes.object,
  isChild: PropTypes.bool,
  isDragging: PropTypes.bool.isRequired,
  isOver: PropTypes.bool.isRequired,
  isReadOnly: PropTypes.bool.isRequired,
  isDraggable: PropTypes.bool,
  isStyleTab: PropTypes.bool.isRequired,
  moveField: PropTypes.func.isRequired,
  setSelectedField: PropTypes.func.isRequired,
  toggleFieldGroupingSelection: PropTypes.func.isRequired,
  createField: PropTypes.func.isRequired
};
FieldsTableRow.defaultProps = {
  canEditFieldType: true,
  canCreateFieldType: true,
  depth: 0,
  fieldName: '',
  isChild: false,
  isDraggable: true
};
const DraggableFieldsTableRow = compose(connect(makeMapStateToProps, mapDispatchToProps), fieldsTableRowDragSource, fieldsTableRowDropTarget)(FieldsTableRow);
export default DraggableFieldsTableRow;